import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ivrEdges } from '@/api/ivrEdges';
import type { IvrEdgeRow } from '@/api/ivrEdges';
import { ivrNodes } from '@/api/ivrNodes';
import type { IvrNodeRow } from '@/api/ivrNodes';
import { NodeTypeBadge } from './NodeTypeBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';
import { Plus, Trash2, ArrowRight } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { describeBranchKey } from './list/styles/common';

// Pill component for a branch key
function BranchKeyPill({ k }: { k: string }) {
  const isSpecial = ['timeout', 'invalid', 'fallback'].includes(k);
  return (
    <span
      title={describeBranchKey(k)}
      className={cn(
        'inline-block rounded border px-1.5 py-0.5 font-mono text-[11px] font-semibold leading-none',
        isSpecial
          ? 'bg-amber-50 border-amber-200 text-amber-700'
          : 'bg-slate-100 border-slate-200 text-slate-700',
      )}
    >
      {k}
    </span>
  );
}

// A single edge row — fetches the to_node lazily
function EdgeRow({
  edge,
  canMutate,
  onDelete,
}: {
  edge: IvrEdgeRow;
  canMutate: boolean;
  onDelete: (id: string) => void;
}) {
  const { data: toNode } = useQuery({
    queryKey: ['ivrNode', edge.to_node_id],
    queryFn: () => ivrNodes.get(edge.to_node_id!),
    enabled: !!edge.to_node_id,
  });

  return (
    <div className="flex items-center gap-2 rounded-md border border-slate-100 bg-white px-3 py-2 text-sm">
      <BranchKeyPill k={edge.branch_key} />
      <ArrowRight className="h-3 w-3 shrink-0 text-slate-300" />
      {toNode ? (
        <span className="flex items-center gap-1.5 min-w-0 flex-1">
          <span className="font-mono text-xs text-slate-500 shrink-0">{formatRef(toNode.ref_number)}</span>
          <span className="truncate text-slate-700">{toNode.name}</span>
          <NodeTypeBadge type={toNode.node_type} className="shrink-0" />
        </span>
      ) : (
        <span className="flex-1 text-xs text-slate-400">
          {edge.to_node_id ? 'Loading…' : '(hang up)'}
        </span>
      )}
      {edge.label && (
        <span className="shrink-0 text-xs text-slate-400 italic">"{edge.label}"</span>
      )}
      {canMutate && (
        <button
          type="button"
          aria-label="Delete edge"
          onClick={() => onDelete(edge.id)}
          className="ml-1 shrink-0 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

interface AddEdgeFormProps {
  fromNodeId: string;
  treeNodes: IvrNodeRow[];
  onAdded: () => void;
}

function AddEdgeForm({ fromNodeId, treeNodes, onAdded }: AddEdgeFormProps) {
  const qc = useQueryClient();
  const [branchKey, setBranchKey] = useState('');
  const [toNodeId, setToNodeId] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');

  const add = useMutation({
    mutationFn: () => ivrEdges.create(fromNodeId, {
      branch_key: branchKey.trim(),
      to_node_id: toNodeId,
      label: label.trim() || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrEdges', fromNodeId] });
      setBranchKey('');
      setToNodeId('');
      setLabel('');
      setError('');
      onAdded();
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : 'Failed to add edge.');
    },
  });

  function submit() {
    if (!branchKey.trim()) { setError('Branch key is required.'); return; }
    if (!toNodeId) { setError('Target node is required.'); return; }
    setError('');
    add.mutate();
  }

  const otherNodes = treeNodes.filter((n) => n.id !== fromNodeId);

  return (
    <div className="mt-3 rounded-md border border-dashed border-slate-200 bg-slate-50 p-3 space-y-2">
      <p className="text-xs font-medium text-slate-600">Add edge</p>
      <div className="flex items-start gap-2 flex-wrap">
        <div className="space-y-1">
          <p className="text-[10px] text-slate-500">Branch key</p>
          <Input
            value={branchKey}
            onChange={(e) => setBranchKey(e.target.value)}
            placeholder="1, timeout, *"
            className="h-8 w-28 font-mono text-xs"
            aria-label="Branch key"
          />
        </div>
        <div className="space-y-1 flex-1 min-w-[160px]">
          <p className="text-[10px] text-slate-500">Target node</p>
          <Select value={toNodeId} onValueChange={setToNodeId}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Pick a node…" />
            </SelectTrigger>
            <SelectContent>
              {otherNodes.map((n) => (
                <SelectItem key={n.id} value={n.id}>
                  <span className="font-mono text-[11px] text-slate-500 mr-1">{formatRef(n.ref_number)}</span>
                  {n.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <p className="text-[10px] text-slate-500">Label (optional)</p>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Main menu"
            className="h-8 w-36 text-xs"
            aria-label="Edge label"
          />
        </div>
        <div className="flex items-end pb-0">
          <Button
            type="button"
            size="sm"
            className="h-8 mt-5"
            disabled={add.isPending}
            onClick={submit}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            {add.isPending ? 'Adding…' : 'Add'}
          </Button>
        </div>
      </div>
      {error && (
        <p role="alert" className="text-xs text-red-500">{error}</p>
      )}
      <p className="text-[10px] text-slate-400">
        Valid keys: digit (0-9), *, #, timeout, invalid, fallback, or any custom string.
      </p>
    </div>
  );
}

interface Props {
  fromNodeId: string;
  treeNodes: IvrNodeRow[];
  canMutate: boolean;
}

export function EdgesList({ fromNodeId, treeNodes, canMutate }: Props) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['ivrEdges', fromNodeId],
    queryFn: () => ivrEdges.listForNode(fromNodeId),
  });

  const deleteEdge = useMutation({
    mutationFn: (id: string) => ivrEdges.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrEdges', fromNodeId] });
    },
  });

  const edges = data?.rows ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Edges ({edges.length})
        </p>
      </div>

      {isLoading ? (
        <div className="text-xs text-slate-400 py-2">Loading edges…</div>
      ) : edges.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">
          No edges yet. Add one below.
        </div>
      ) : (
        <div className="space-y-1.5">
          {edges.map((edge) => (
            <EdgeRow
              key={edge.id}
              edge={edge}
              canMutate={canMutate}
              onDelete={(id) => deleteEdge.mutate(id)}
            />
          ))}
        </div>
      )}

      {canMutate && (
        <AddEdgeForm
          fromNodeId={fromNodeId}
          treeNodes={treeNodes}
          onAdded={() => qc.invalidateQueries({ queryKey: ['ivrEdges', fromNodeId] })}
        />
      )}
    </div>
  );
}
